import React, { useState, useEffect, useCallback } from "react";
import {
  snapshotsApi, discussionApi, countersApi, theoriesApi,
  branchesApi, learningsApi, bookmarksApi,
} from "../../lib/api";
import { toast } from "sonner";
import {
  X, Bookmark, ArrowSquareOut, CaretDown, CaretRight,
  ChatCircle, GitFork, Lightbulb, ArrowUp, ArrowDown,
  CheckCircle, Plus, PaperPlaneRight, FlowArrow,
} from "@phosphor-icons/react";
import { Dialog, DialogContent } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import RatingOvals from "../RatingOvals";

// ── Design tokens ─────────────────────────────────────────────────────────────

const BRANCH_TYPE_LABELS = {
  adjacent_industry:   "Adjacent Industry",
  opposing_view:       "Opposing View",
  regional_comparison: "Regional Comparison",
  expansion_path:      "Expansion Path",
  hard_statistics:     "Hard Statistics",
  challenge_assumption:"Challenge Assumption",
  content_angle:       "Content Angle",
  visual_angle:        "Visual Angle",
};

const MSG_TYPE_COLORS = {
  comment:          "text-slate-600",
  challenge:        "text-rose-600",
  evidence:         "text-blue-600",
  research_request: "text-amber-600",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function Section({ title, icon: Icon, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-6 py-3.5 hover:bg-slate-50 transition-colors text-left"
      >
        {Icon && <Icon size={15} className="text-slate-500 shrink-0" />}
        <span className="text-[13px] font-semibold text-slate-800 flex-1">{title}</span>
        {count != null && (
          <span className="text-[12px] font-mono text-slate-400 mr-2">{count}</span>
        )}
        {open ? (
          <CaretDown size={12} className="text-slate-400" />
        ) : (
          <CaretRight size={12} className="text-slate-400" />
        )}
      </button>
      {open && <div className="px-6 pb-5">{children}</div>}
    </div>
  );
}

function ConfidenceBar({ score, prev }) {
  const color = score >= 80 ? "bg-emerald-500" : score >= 60 ? "bg-amber-500" : "bg-rose-500";
  const diff  = prev != null ? score - prev : null;
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <div className="flex items-center gap-1">
        <span className="font-mono text-xs font-semibold text-slate-700">{score}</span>
        {diff != null && diff !== 0 && (
          <span className={`flex items-center text-[10px] font-mono ${diff > 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {diff > 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
            {Math.abs(diff)}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Discussion ────────────────────────────────────────────────────────────────
function DiscussionSection({ snapshotId }) {
  const [messages, setMessages] = useState([]);
  const [content,  setContent]  = useState("");
  const [msgType,  setMsgType]  = useState("comment");
  const [posting,  setPosting]  = useState(false);

  useEffect(() => {
    discussionApi.list(snapshotId).then((r) => setMessages(r.data)).catch(() => {});
  }, [snapshotId]);

  const post = async () => {
    if (!content.trim()) return;
    setPosting(true);
    try {
      const { data } = await discussionApi.post(snapshotId, { content, message_type: msgType });
      setMessages((m) => [...m, data]);
      setContent("");
    } catch { toast.error("Failed to post message"); }
    finally { setPosting(false); }
  };

  return (
    <div className="space-y-3">
      {messages.length === 0 && (
        <p className="text-[13px] text-slate-400 italic">No discussion yet.</p>
      )}
      {messages.map((m) => (
        <div key={m.id} className="flex gap-3">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold mt-0.5 ${
            m.author_type === "hermes" ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-600"
          }`}>
            {m.author_type === "hermes" ? "H" : "OP"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-[12px] font-semibold text-slate-700 capitalize">
                {m.author_type === "hermes" ? "Hermes" : "Operator"}
              </span>
              <span className={`text-[11px] font-medium uppercase tracking-wider ${MSG_TYPE_COLORS[m.message_type]}`}>
                {m.message_type?.replace("_", " ")}
              </span>
              <span className="text-[11px] font-mono text-slate-400 ml-auto">
                {new Date(m.created_at).toLocaleDateString("en-AU", { day:"numeric", month:"short" })}
              </span>
            </div>
            <p className="text-[13px] text-slate-700 leading-relaxed">{m.content}</p>
          </div>
        </div>
      ))}

      {/* Compose */}
      <div className="border border-slate-200 rounded-lg overflow-hidden mt-3">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Add to discussion…"
          rows={3}
          className="w-full px-3 py-2.5 text-[13px] text-slate-800 resize-none focus:outline-none placeholder:text-slate-400"
        />
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-t border-slate-200">
          <select
            value={msgType}
            onChange={(e) => setMsgType(e.target.value)}
            className="text-[12px] font-medium text-slate-600 bg-transparent focus:outline-none"
          >
            <option value="comment">Comment</option>
            <option value="challenge">Challenge</option>
            <option value="evidence">Evidence</option>
            <option value="research_request">Research Request</option>
          </select>
          <button
            onClick={post}
            disabled={posting || !content.trim()}
            className="ml-auto flex items-center gap-1.5 text-[12px] font-semibold text-white bg-slate-900 px-3 py-1.5 rounded-md disabled:opacity-40 hover:bg-slate-700 transition-colors"
          >
            <PaperPlaneRight size={13} />
            Post
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Branches ──────────────────────────────────────────────────────────────────
function BranchesSection({ snapshotId }) {
  const [branches,  setBranches]  = useState([]);
  const [showForm,  setShowForm]  = useState(false);
  const [form,      setForm]      = useState({ branch_type: "adjacent_industry", title: "", content: "", confidence_score: 70 });
  const [saving,    setSaving]    = useState(false);

  useEffect(() => {
    branchesApi.list(snapshotId).then((r) => setBranches(r.data)).catch(() => {});
  }, [snapshotId]);

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      const { data } = await branchesApi.create(snapshotId, form);
      setBranches((b) => [...b, data]);
      setShowForm(false);
      setForm({ branch_type: "adjacent_industry", title: "", content: "", confidence_score: 70 });
    } catch { toast.error("Failed to create branch"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-2.5">
      {branches.length === 0 && !showForm && (
        <p className="text-xs text-slate-400 italic">No branches yet.</p>
      )}
      {branches.map((b) => (
        <div key={b.id} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-white border border-slate-200 px-2 py-0.5 rounded-md">
              {BRANCH_TYPE_LABELS[b.branch_type] || b.branch_type}
            </span>
            <span className={`ml-auto text-[12px] font-mono font-bold ${
              b.confidence_score >= 70 ? "text-emerald-600" : "text-amber-600"
            }`}>
              {b.confidence_score}
            </span>
          </div>
          <h4 className="text-[14px] font-semibold text-slate-900 mb-1.5">{b.title}</h4>
          <p className="text-[13px] text-slate-600 leading-relaxed">{b.content}</p>
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-slate-200">
            <span className={`text-[11px] font-medium ${b.created_by_type === "hermes" ? "text-blue-600" : "text-slate-500"}`}>
              {b.created_by_type === "hermes" ? "Hermes" : "Operator"}
            </span>
            <span className="text-[11px] font-mono text-slate-400 ml-auto">
              {new Date(b.created_at).toLocaleDateString("en-AU", { day:"numeric", month:"short" })}
            </span>
          </div>
        </div>
      ))}

      {showForm ? (
        <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-white">
          <select
            value={form.branch_type}
            onChange={(e) => setForm((f) => ({ ...f, branch_type: e.target.value }))}
            className="w-full text-[13px] border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            {Object.entries(BRANCH_TYPE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Branch title"
            className="w-full text-[13px] border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-300"
          />
          <textarea
            value={form.content}
            onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
            placeholder="Analysis and supporting reasoning…"
            rows={4}
            className="w-full text-[13px] border border-slate-200 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-slate-300"
          />
          <div className="flex items-center gap-3">
            <label className="text-[12px] text-slate-500 font-medium">Confidence</label>
            <input
              type="number" min="0" max="100"
              value={form.confidence_score}
              onChange={(e) => setForm((f) => ({ ...f, confidence_score: Number(e.target.value) }))}
              className="w-16 text-[13px] border border-slate-200 rounded-md px-2.5 py-1.5 font-mono focus:outline-none"
            />
            <button onClick={() => setShowForm(false)} className="ml-auto text-[13px] text-slate-500 hover:text-slate-700 font-medium">Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1.5 text-[13px] font-semibold text-white bg-slate-900 px-4 py-1.5 rounded-md disabled:opacity-40 hover:bg-slate-700 transition-colors"
            >
              <GitFork size={14} />
              {saving ? "Saving…" : "Create branch"}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 text-[13px] font-semibold text-slate-700 border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2 rounded-md transition-colors"
        >
          <Plus size={14} />
          New branch
        </button>
      )}
    </div>
  );
}

// ── Working theory ────────────────────────────────────────────────────────────
function TheorySection({ snapshotId, originalConfidence }) {
  const [theories, setTheories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [synthesis, setSynthesis] = useState("");
  const [confidence, setConfidence] = useState(70);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    theoriesApi.list(snapshotId).then((r) => setTheories(r.data)).catch(() => {});
  }, [snapshotId]);

  const latest = theories[0];

  const save = async () => {
    if (!synthesis.trim()) return;
    setSaving(true);
    try {
      const { data } = await theoriesApi.create(snapshotId, { synthesis, confidence_score: confidence });
      setTheories((t) => [data, ...t]);
      setShowForm(false);
      setSynthesis("");
    } catch { toast.error("Failed to save theory"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      {!latest && !showForm && (
        <p className="text-[13px] text-slate-400 italic">No working theory yet.</p>
      )}
      {latest && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] font-bold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-md">
              v{latest.version}
            </span>
            <span className="text-[12px] font-medium text-slate-500 capitalize">
              {latest.generated_by === "hermes" ? "Hermes" : "Operator"}
            </span>
            <span className="text-[12px] font-mono text-slate-400 ml-auto">
              {new Date(latest.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
            </span>
          </div>
          <ConfidenceBar score={latest.confidence_score} prev={originalConfidence} />
          <p className="text-[13px] text-slate-700 leading-relaxed mt-3">{latest.synthesis}</p>
          {theories.length > 1 && (
            <details className="mt-3">
              <summary className="text-[12px] text-slate-400 cursor-pointer hover:text-slate-600 font-medium">
                {theories.length - 1} earlier version{theories.length > 2 ? "s" : ""}
              </summary>
              <div className="mt-2 space-y-3 pl-4 border-l-2 border-slate-200 mt-2">
                {theories.slice(1).map((t) => (
                  <div key={t.id}>
                    <span className="text-[11px] font-mono text-slate-400">v{t.version} · {new Date(t.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
                    <p className="text-[13px] text-slate-500 leading-relaxed mt-0.5 line-clamp-2">{t.synthesis}</p>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {showForm ? (
        <div className="space-y-3 border border-slate-200 rounded-lg p-4 bg-white">
          <textarea
            value={synthesis}
            onChange={(e) => setSynthesis(e.target.value)}
            placeholder="Working theory synthesis…"
            rows={5}
            className="w-full text-[13px] border border-slate-200 rounded-md px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-slate-300"
          />
          <div className="flex items-center gap-3">
            <label className="text-[12px] text-slate-500 font-medium">Confidence</label>
            <input
              type="number" min="0" max="100"
              value={confidence}
              onChange={(e) => setConfidence(Number(e.target.value))}
              className="w-16 text-[13px] border border-slate-200 rounded-md px-2.5 py-1.5 font-mono focus:outline-none"
            />
            <button onClick={() => setShowForm(false)} className="ml-auto text-[13px] text-slate-500 hover:text-slate-700 font-medium">Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1.5 text-[13px] font-semibold text-white bg-purple-700 px-4 py-1.5 rounded-md disabled:opacity-40 hover:bg-purple-800 transition-colors"
            >
              <Lightbulb size={14} />
              {saving ? "Saving…" : "Save theory"}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 text-[13px] font-semibold text-purple-700 border border-purple-300 bg-purple-50 hover:bg-purple-100 px-4 py-2 rounded-md transition-colors"
        >
          <Plus size={14} />
          {latest ? "New version" : "Add working theory"}
        </button>
      )}
    </div>
  );
}

// ── Promote ───────────────────────────────────────────────────────────────────
function PromoteSection({ snapshot, onPromoted }) {
  const [form,    setForm]    = useState({ title: snapshot.title, learning: "", confidence: 80, tags: [] });
  const [saving,  setSaving]  = useState(false);
  const [promoted,setPromoted]= useState(!!snapshot.institutional_learning);

  if (promoted) {
    return (
      <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md">
        <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0" />
        <span className="text-xs text-emerald-700 font-medium">Promoted to Institutional Learning</span>
      </div>
    );
  }

  const promote = async () => {
    if (!form.learning.trim()) return;
    setSaving(true);
    try {
      await learningsApi.promote({
        ...form,
        source_snapshot_ids: [snapshot.id],
      });
      setPromoted(true);
      onPromoted?.();
      toast.success("Promoted to Institutional Learning");
    } catch { toast.error("Failed to promote"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-slate-600">Distil this insight into an operator-validated institutional learning — it will feed directly into Hermes's retrieval chain at highest priority.</p>
      <input
        value={form.title}
        onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
        placeholder="Learning title"
        className="w-full text-[13px] border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300"
      />
      <textarea
        value={form.learning}
        onChange={(e) => setForm((f) => ({ ...f, learning: e.target.value }))}
        placeholder="Distilled learning statement — a specific, actionable fact or principle..."
        rows={4}
        className="w-full text-[13px] border border-slate-200 rounded-md px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-300"
      />
      <div className="flex items-center gap-3">
        <label className="text-[12px] font-medium text-slate-500">Confidence</label>
        <input
          type="number" min="0" max="100"
          value={form.confidence}
          onChange={(e) => setForm((f) => ({ ...f, confidence: Number(e.target.value) }))}
          className="w-16 font-mono text-[13px] border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none"
        />
        <button
          onClick={promote}
          disabled={saving || !form.learning.trim()}
          className="ml-auto flex items-center gap-2 text-[13px] font-semibold text-white bg-emerald-700 px-4 py-2 rounded-md disabled:opacity-40 hover:bg-emerald-800 transition-colors"
        >
          <CheckCircle size={15} weight="fill" />
          {saving ? "Promoting…" : "Promote to Institutional Learning"}
        </button>
      </div>
    </div>
  );
}

// ── Rating section ────────────────────────────────────────────────────────────
function RatingSection({ snap, onUpdated }) {
  const [note,    setNote]    = useState(snap.operator_note || "");
  const [saving,  setSaving]  = useState(false);
  const [showNote,setShowNote]= useState(!!snap.operator_note);

  const saveRating = async (rating) => {
    setSaving(true);
    try {
      const { data } = await snapshotsApi.rate(snap.id, { rating, note: note || null });
      onUpdated(data);
      toast.success(rating ? "Rating saved" : "Rating cleared");
    } catch { toast.error("Failed to save rating"); }
    finally { setSaving(false); }
  };

  const saveNote = async () => {
    setSaving(true);
    try {
      const { data } = await snapshotsApi.rate(snap.id, { rating: snap.operator_rating, note: note || null });
      onUpdated(data);
      toast.success("Note saved");
    } catch { toast.error("Failed to save note"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-b border-slate-200 px-6 py-3 shrink-0 bg-slate-50">
      <div className="flex items-center gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Signal quality
        </span>
        <RatingOvals
          value={snap.operator_rating}
          onChange={saveRating}
          compact={false}
          disabled={saving}
        />
        <button
          onClick={() => setShowNote((s) => !s)}
          className="ml-auto text-[12px] text-slate-400 hover:text-slate-600 transition-colors"
        >
          {showNote ? "Hide note" : snap.operator_note ? "Edit note" : "Add note"}
        </button>
      </div>
      {showNote && (
        <div className="flex items-center gap-2 mt-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={saveNote}
            onKeyDown={(e) => e.key === "Enter" && saveNote()}
            placeholder="What worked or didn't about this signal?"
            className="flex-1 text-[13px] border border-slate-200 rounded-md px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-slate-200 placeholder:text-slate-400"
          />
        </div>
      )}
    </div>
  );
}


// ── Main Workbench ────────────────────────────────────────────────────────────
export default function IntelligenceWorkbench({ snapshotId, onClose, onBookmarkToggle, bookmarked }) {
  const [snap,    setSnap]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [versions,setVersions]= useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: v }] = await Promise.all([
        snapshotsApi.get(snapshotId),
        snapshotsApi.versions(snapshotId),
      ]);
      setSnap(s);
      setVersions(v);
    } catch { toast.error("Failed to load intelligence object"); }
    finally { setLoading(false); }
  }, [snapshotId]);

  useEffect(() => { load(); }, [load]);

  if (!snap && !loading) return null;

  return (
    <Dialog open={!!snapshotId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-[860px] w-[92vw] h-[88vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden"
      >
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-[13px] text-slate-400">
            Loading…
          </div>
        ) : snap ? (
          <>
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="border-b border-slate-200 px-6 py-4 shrink-0">
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <Badge variant={snap.signal_type || "opportunity"}>
                      {(snap.signal_type || "OPPORTUNITY").toUpperCase()}
                    </Badge>
                    {snap.geo && (
                      <span className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                        {snap.geo}
                      </span>
                    )}
                    <span className="text-[11px] font-mono text-slate-400 ml-auto">
                      {new Date(snap.created_at).toLocaleDateString("en-AU", {
                        day: "numeric", month: "short", year: "numeric",
                      })}
                    </span>
                  </div>
                  <h2 className="font-display text-[18px] font-bold text-slate-900 leading-snug tracking-tight">
                    {snap.title}
                  </h2>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => onBookmarkToggle?.(snap)}
                    title="Vault"
                    className="h-8 w-8"
                  >
                    <Bookmark size={15} weight={bookmarked ? "fill" : "regular"} className={bookmarked ? "text-slate-700" : "text-slate-400"} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={onClose} title="Close" className="h-8 w-8">
                    <X size={15} className="text-slate-500" />
                  </Button>
                </div>
              </div>

              <ConfidenceBar score={snap.confidence_score} />

              {snap.tags?.length > 0 && (
                <div className="flex gap-1.5 flex-wrap mt-3">
                  {snap.tags.map((t) => (
                    <span key={t} className="text-[11px] font-mono uppercase tracking-wider text-slate-500 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* ── Rating row ──────────────────────────────────────────────── */}
            <RatingSection snap={snap} onUpdated={(updated) => setSnap(updated)} />

            {/* ── Scrollable body ──────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto">

              {/* Strategic Angle */}
              <div className="px-6 py-5 border-b border-slate-100 border-l-4 border-l-blue-500">
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-blue-600 mb-2">
                  Strategic Angle
                </div>
                <p className="text-[15px] font-medium text-slate-800 leading-relaxed">
                  {snap.summary}
                </p>
              </div>

              {/* Why It Matters */}
              {snap.full_analysis && (
                <div className="px-6 py-5 border-b border-slate-100">
                  <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">
                    Why It Matters
                  </div>
                  <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-line">
                    {snap.full_analysis}
                  </p>
                </div>
              )}

              {/* Citations */}
              {snap.evidence?.length > 0 && (
                <Section title="Citations" count={snap.evidence.length} icon={ArrowSquareOut} defaultOpen>
                  <div className="space-y-3">
                    {snap.evidence.map((ev) => (
                      <div key={ev.id} className="flex gap-2.5 items-start">
                        <Badge variant={ev.trust_tier || "media"} className="shrink-0 mt-0.5">
                          {(ev.trust_tier || "media").toUpperCase()}
                        </Badge>
                        <div className="min-w-0">
                          {ev.source_url ? (
                            <a href={ev.source_url} target="_blank" rel="noreferrer"
                              className="text-[13px] font-medium text-blue-600 hover:underline leading-tight block truncate">
                              {ev.title || ev.source_url}
                            </a>
                          ) : (
                            <span className="text-[13px] font-medium text-slate-700">{ev.title}</span>
                          )}
                          {ev.extracted_content && (
                            <p className="text-[12px] text-slate-500 mt-0.5 line-clamp-2 leading-relaxed">
                              {ev.extracted_content}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Counter-hypotheses */}
              {snap.counter_hypotheses?.length > 0 && (
                <Section title="Contrarian Interpretation" count={snap.counter_hypotheses.length} icon={GitFork} defaultOpen>
                  <div className="space-y-3">
                    {snap.counter_hypotheses.map((ch) => (
                      <div key={ch.id} className="border-l-4 border-l-rose-400 pl-4">
                        <div className="flex items-center gap-2 mb-1.5">
                          <h4 className="text-[14px] font-semibold text-slate-800">{ch.title}</h4>
                          <Badge variant={ch.status === "active" ? "contrarian" : ch.status === "merged" ? "theory" : "secondary"}>
                            {ch.status}
                          </Badge>
                        </div>
                        <p className="text-[13px] text-slate-600 leading-relaxed">{ch.hypothesis}</p>
                        {ch.supporting_evidence && (
                          <p className="text-[12px] text-slate-500 mt-1.5 italic">{ch.supporting_evidence}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Branches */}
              <Section title="Branches" icon={FlowArrow} defaultOpen={false}>
                <BranchesSection snapshotId={snap.id} />
              </Section>

              {/* Discussion */}
              <Section
                title="Discussion"
                count={snap.discussion?.length || 0}
                icon={ChatCircle}
                defaultOpen={snap.discussion?.length > 0}
              >
                <DiscussionSection snapshotId={snap.id} />
              </Section>

              {/* Working Theory */}
              <Section title="Working Theory" icon={Lightbulb} defaultOpen>
                <TheorySection snapshotId={snap.id} originalConfidence={snap.confidence_score} />
              </Section>

              {/* Promote */}
              <Section title="Promote to Learning" icon={CheckCircle} defaultOpen={false}>
                <PromoteSection snapshot={snap} onPromoted={load} />
              </Section>

              {/* Version History */}
              {versions.length > 1 && (
                <Section title="Version History" count={versions.length} defaultOpen={false}>
                  <div className="space-y-2">
                    {versions.map((v) => (
                      <div key={v.id} className="flex items-center gap-3">
                        <span className="font-mono text-[11px] text-slate-400 w-6">v{v.version}</span>
                        <div className="flex-1 h-px bg-slate-100" />
                        <span className="text-[12px] text-slate-500">{v.created_by}</span>
                        <span className="font-mono text-[11px] text-slate-400">
                          {new Date(v.created_at).toLocaleDateString("en-AU", { day:"numeric", month:"short", year:"numeric" })}
                        </span>
                        <span className={`font-mono text-[12px] font-semibold ${
                          v.confidence_score >= 80 ? "text-emerald-600" :
                          v.confidence_score >= 60 ? "text-amber-600" : "text-rose-600"
                        }`}>{v.confidence_score}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              <div className="h-10" />
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
